// src/pages/controleBar/comandas/FecharComandaModal.js
import React, { useState, useEffect, useRef } from "react";
import "./FecharComandaModal.css";
import { useComanda } from "../../../contexts/controleBar/comandas/ComandaContext";
import { useAlert } from "../../../contexts/geral/AlertContext";
import { Nota } from "../../../models/vendas/Nota";
import { usePrinter } from '../../../contexts/geral/PrinterContext';
import ModalFinalizarVenda from "../../vendas/ModalFinalizarVenda";
import { formatNumeric, parseNumeric, formatCurrency, fromMoneyCents, sumMoney, toMoneyCents } from "../../../utils/numberUtils";
import { useNavigate } from "react-router-dom";
import { StatusComanda } from "../../../models/controleBar/comandas/StatusComanda";
import ModalPortal from '../../../components/modalForm/ModalPortal';
import { montarNotaMesaPDV, ratearTotalMesaPDV } from '../../../services/vendas/PDVService';


const FecharComandaModal = ({ open, onClose, comanda, onPrint }) => {

  const navigate = useNavigate();
  const { imprimirComprovantesQuitados } = usePrinter();
  const comandasAntesPagamento = useRef([]);

  const { showAlert } = useAlert();
  const { fetchComandas } = useComanda();

  const [valores, setValores] = useState({});
  const [total, setTotal] = useState("0,00");

  const [totalRestante, setTotalRestante] = useState("0,00");



  // 🔹 Estado do modal de finalizar venda
  const [showFinalizarVenda, setShowFinalizarVenda] = useState(false);
  const [nota, setNota] = useState(new Nota());

  useEffect(() => {
    if (comanda?.comandaItens) {
      const inicial = {};
      //let somaRestante = 0;
      const valoresComanda = [];

      comanda.comandaItens.forEach((i) => {
        const restante = fromMoneyCents(Math.max(0, toMoneyCents(i.valorTotal) - toMoneyCents(i.valorPago)));

        valoresComanda.push(i.valorTotal);
        //somaRestante += restante;

        inicial[i.id] = formatNumeric(restante);
      });

      const somaComanda = sumMoney(valoresComanda);

      setValores(inicial);
      // Total (valor total da comanda)

      const totalPago = sumMoney(comanda.comandaItens.map(i => i.valorPago));

      // 👉 Valor restante da comanda
      const valorRestante = fromMoneyCents(toMoneyCents(somaComanda) - toMoneyCents(totalPago));

      // 🔹 Valor a Pagar já começa com o restante
      setTotal(formatNumeric(valorRestante));

      // 🔹 Restante começa zerado (vai mudar conforme edição)
      setTotalRestante("0,00");


    }
  }, [comanda]);



  if (!open || !comanda) return null;

  const handleFocusSelectAll = (e) => {
    e.target.select();
  };

  const handleBlurFormatItem = (id) => {
    const valorNum = parseNumeric(valores[id]);
    const item = comanda.comandaItens.find(i => i.id === id);
    const saldoItem = item
      ? fromMoneyCents(Math.max(0, toMoneyCents(item.valorTotal) - toMoneyCents(item.valorPago)))
      : 0;
    if (toMoneyCents(valorNum) > toMoneyCents(saldoItem)) {
      showAlert('O valor a pagar não pode superar o saldo do item.', 'warning');
      handleChangeItem(id, formatNumeric(saldoItem));
      return;
    }
    setValores((prev) => ({
      ...prev,
      [id]: formatNumeric(valorNum),
    }));
  };

  const handleBlurFormatTotal = () => {
    const valorNum = parseNumeric(total);
    try {
      const rateio = ratearTotalMesaPDV({ comandas: [comanda], total: valorNum });
      setValores(Object.fromEntries(
        Object.entries(rateio).map(([id, valor]) => [id, formatNumeric(valor)])
      ));
      setTotal(formatNumeric(valorNum));
    } catch (error) {
      showAlert(error.message, 'warning');
      const totalValido = sumMoney(Object.values(valores));
      setTotal(formatNumeric(totalValido));
      const saldoComanda = sumMoney(comanda.comandaItens.map(i =>
        fromMoneyCents(Math.max(0, toMoneyCents(i.valorTotal) - toMoneyCents(i.valorPago)))
      ));
      setTotalRestante(formatNumeric(fromMoneyCents(toMoneyCents(saldoComanda) - toMoneyCents(totalValido))));
    }
  };


  const handleChangeItem = (id, value) => {
    const valorStr = value.replace(/[^\d,]/g, "");

    const novoValores = {
      ...valores,
      [id]: valorStr,
    };

    setValores(novoValores);

    const soma = sumMoney(Object.values(novoValores));

    setTotal(formatNumeric(soma));

    // 🔹 recalcular total restante
    const saldoComanda = sumMoney(comanda.comandaItens.map(i =>
      fromMoneyCents(Math.max(0, toMoneyCents(i.valorTotal) - toMoneyCents(i.valorPago)))
    ));
    const restante = fromMoneyCents(toMoneyCents(saldoComanda) - toMoneyCents(soma));

    setTotalRestante(formatNumeric(restante));
  };



  const handleChangeTotal = (value) => {
    const valorStr = value.replace(/[^\d,]/g, "");
    setTotal(valorStr);

    const novoTotal = parseNumeric(valorStr);

    try {
      // O rateio compartilhado mantém a soma exata em centavos e ignora itens
      // quitados, inclusive ao distribuir o último centavo.
      const rateio = ratearTotalMesaPDV({ comandas: [comanda], total: novoTotal });
      setValores(Object.fromEntries(
        Object.entries(rateio).map(([id, valor]) => [id, formatNumeric(valor)])
      ));
      const saldoComanda = sumMoney(comanda.comandaItens.map(i =>
        fromMoneyCents(Math.max(0, toMoneyCents(i.valorTotal) - toMoneyCents(i.valorPago)))
      ));
      setTotalRestante(formatNumeric(fromMoneyCents(toMoneyCents(saldoComanda) - toMoneyCents(novoTotal))));
    } catch (_) {
      // O valor incompleto continua visível durante a digitação e será
      // validado no blur ou antes de abrir a finalização.
    }
  };




  const handleConfirmar = async () => {
    comandasAntesPagamento.current = ([comanda] || []).map(c => ({ id: c.id, status: c.status }));
    try {
      const notaPayload = montarNotaMesaPDV({
        clienteId: comanda.clienteId || 1,
        comandas: [comanda],
        valores
      });
      setNota(notaPayload);
      setShowFinalizarVenda(true);
    } catch (error) {
      showAlert(error.message, 'warning');
    }
  };

  const totalColunaTotal = sumMoney(comanda.comandaItens.map(i => i.valorTotal));

  const totalColunaPago = sumMoney(comanda.comandaItens.map(i => i.valorPago));

  const totalColunaValorAPagar = sumMoney(Object.values(valores));

  return (
    

    <ModalPortal show={open}>
    <div className="modal-overlay">
      <div className="modal-container">
        <h2>Fechar Comanda #{comanda.codigo}</h2>

        <div className="modal-body">
          <table className="modal-tabela">
            <thead>
              <tr>
                <th>Item</th>
                <th>Descrição</th>
                <th>Qtde</th>
                <th>Total</th>
                <th>Pago</th>
                <th>Valor a Pagar</th>
              </tr>
            </thead>
            <tbody>
              {comanda.comandaItens.map((i) => (
                <tr key={i.id}>
                  <td>{i.item}</td>
                  <td style={{minWidth:'220px'}}>{i.produto?.descricao || i.xProd}</td>
                  <td style={{textAlign:'right'}}>{i.qtde}</td>
                  <td style={{textAlign:'right'}}>{formatCurrency(i.valorTotal)}</td>
                  <td style={{textAlign:'right'}}>{formatCurrency(i.valorPago)}</td>
                  <td >
                    <input
                      className="input-valor"
                      style={{ maxWidth: "100px", textAlign: "right" }}
                      type="text"
                      value={valores[i.id] || "0,00"}
                      onChange={(e) => handleChangeItem(i.id, e.target.value)}
                      onFocus={handleFocusSelectAll}
                      onBlur={() => handleBlurFormatItem(i.id)}
                      inputMode="decimal"
                    />

                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="tfoot-row">
                <td></td>
                <td style={{ fontWeight: "bold" }}>Totais:</td>

                {/* Qtde não soma */}
                <td></td>

                {/* Total */}
                <td style={{ textAlign: "right", fontWeight: "bold" }}>
                  {formatCurrency(totalColunaTotal)}
                </td>

                {/* Pago */}
                <td style={{ textAlign: "right", fontWeight: "bold" }}>
                  {formatCurrency(totalColunaPago)}
                </td>

                {/* Valor a pagar */}
                <td style={{ textAlign: "right", fontWeight: "bold" }}>
                  {formatCurrency(totalColunaValorAPagar)}
                </td>
              </tr>
            </tfoot>

          </table>
        </div>

        <div className="modal-footer">
          <div className="totais-box">

            {/* 🔹 TOTAL */}
            {/* <div className="linha-total">
              <label>Total:</label>
              <span className="valor-total">{totalComanda}</span>
            </div> */}

            {/* 🔹 INPUT DO PAGAMENTO */}
            <div className="linha-total">
              <label>Valor a Pagar:</label>
              <input
                style={{
                  padding: "0px",
                  paddingRight: "2px",
                  paddingLeft: "2px",
                  textAlign: "right",
                }}
                type="text"
                value={total}
                onChange={(e) => handleChangeTotal(e.target.value)}
                onFocus={handleFocusSelectAll}
                onBlur={handleBlurFormatTotal}
                inputMode="decimal"
              />
            </div>

            {/* 🔹 RESTANTE */}
            {totalRestante!=="0,00" && (
              <div className="linha-total">
                <label>Restante:</label>
                <span className="valor-total">{totalRestante}</span>
              </div>
            )}

            

          </div>


          <div className="modal-actions">
            <button className="btn-cancelar" onClick={onClose}>
              Cancelar
            </button>
            <button className="btn-print" onClick={() => onPrint(comanda, valores)}>
              Imprimir
            </button>
            <button className="btn-confirmar" onClick={handleConfirmar}>
              Confirmar
            </button>
          </div>
        </div>
      </div>
      <ModalFinalizarVenda
        show={showFinalizarVenda}
        onHide={() => setShowFinalizarVenda(false)}
        nota={nota}
        setNota={setNota}
        permitirEditarItens={true}
        onConfirm={async () => {
          const resultComandas = await fetchComandas(comanda.mesaId);
          await imprimirComprovantesQuitados(comandasAntesPagamento.current, resultComandas);
          const todasComandasFechadas = resultComandas.every(c => c.status === StatusComanda.Fechada);

          setShowFinalizarVenda(false);
          onClose();

          if (todasComandasFechadas) {
            showAlert(
              `Mesa #${comanda.mesaId} finalizada com sucesso!`,
              "success"
            );
            navigate("/mesaPage"); // ✅ volta para mesas
          } else {
            showAlert(
              `Comanda #${comanda.codigo} finalizada com sucesso!`,
              "success"
            );
          }
        }}

      />


    </div>
    </ModalPortal>
  );
};

export default FecharComandaModal;
